import { useEffect, useRef, SyntheticEvent } from "react";
import {
  State,
  initialState,
  Actions,
  ActionTypes,
  reducer,
  EnabledState
} from "./State";
import { useTranslation } from "utilities/i18n";
import { DISMISS, useNotification } from "collections";
import {
  useCreatePolicyMutation,
  useDeviceParametersQuery,
  usePoliciesQuery,
  useSetPcapEnabledMutation,
  DeviceParametersQuery,
  PoliciesQuery,
  PolicyInput,
  PolicyDeploymentScope,
  PolicyType,
  SwUpdateSource,
  useProvisioningSyncMutation
} from "generated/graphql";
import { useTypesafeActions } from "store";
import { useAuth0 } from "@auth0/auth0-react";
import { SECOND_IN_MILLISECONDS } from "Constants";

export function getExistingPolicy(data: PoliciesQuery | undefined) {
  const policies = data?.getPolicies?.filter(value => {
    return (
      value?.policyType === "DEVICE_SETTINGS_UPDATE" &&
      value?.policyDeploymentScope === "DEVICE"
    );
  });
  return policies?.length === 1 ? policies[0] : null;
}

export function parseParameters(
  data: DeviceParametersQuery | undefined,
  timer: number,
  previousState: EnabledState,
  fetching: boolean
) {
  const parameters = data?.getDeviceParametersExtended?.filter(value => {
    return value?.name === "diags.pcap.enabled";
  });
  const parameter = parameters?.length === 1 ? parameters[0] : null;
  const currentValue = parameter?.policyAttribute?.currentValue ?? "0";
  const enabled: boolean = currentValue === "1";
  // Set loading flag(/show loading message) only while fetching config parameters for the first time.
  const loading = fetching && !data;
  const failed =
    parameters == null ||
    (parameter != null &&
      parameter.policyAttribute != null &&
      parameter.policyAttribute?.currentValue == null);
  const enabling =
    previousState === EnabledState.Disabled ||
    previousState === EnabledState.Enabling;
  let enabledState = EnabledState.Disabled;
  if (loading) {
    enabledState = EnabledState.Loading;
  } else if (failed) {
    enabledState = EnabledState.Failed;
  } else if (timer > 0) {
    if (enabling) {
      enabledState = EnabledState.Enabling;
    } else {
      enabledState = EnabledState.Disabling;
    }
  } else if (enabled) {
    enabledState = EnabledState.Enabled;
  } else {
    enabledState = EnabledState.Disabled;
  }
  return {
    enabledState
  };
}

export function usePcapEnabledLogic(
  deviceId: string,
  tenantId: string,
  deviceModel: string,
  siteId: string,
  connected: boolean,
  timeout: number,
  delay?: number
) {
  const { t } = useTranslation();
  const [addNotification] = useNotification();
  const [, createPolicy] = useCreatePolicyMutation();
  const [, setPCAPEnabled] = useSetPcapEnabledMutation();
  const [, provisioningSync] = useProvisioningSyncMutation();
  const { user } = useAuth0();

  const [{ enabledState }, actions] = useTypesafeActions<State, ActionTypes>(
    reducer,
    initialState,
    Actions
  );
  const interval = delay ? delay : 5 * SECOND_IN_MILLISECONDS;

  const { data, error, fetching } = useDeviceParametersQuery({
    variables: {
      deviceId,
      tenantId,
      deviceModel,
      siteId,
      sendOnlyChanged: false
    },
    pause: !connected,
    requestPolicy: "network-only",
    pollInterval: interval
  });

  let timer = useRef<number>(0);

  const { enabledState: newEnabledState } = parseParameters(
    data,
    timer.current,
    enabledState,
    fetching
  );

  useEffect(() => {
    if (
      connected &&
      (error || (data && newEnabledState === EnabledState.Failed))
    ) {
      addNotification({
        id: "device-pcap-enabled",
        type: "error",
        message: t("PCAP_ENABLED_ERROR"),
        dismiss: DISMISS.DEFAULT
      });
    }
  });

  useEffect(() => {
    if (newEnabledState !== enabledState) {
      actions.updateEnabledState(newEnabledState);
    }
  });

  const { data: policies } = usePoliciesQuery({
    variables: {
      scope: PolicyDeploymentScope.Device,
      tenantId,
      deviceId,
      deviceModel,
      siteId
    },
    pause: !connected,
    requestPolicy: "network-only",
    pollInterval: interval
  });
  const policy = getExistingPolicy(policies);

  const handleEnabledChangeRequested = async (_event?: SyntheticEvent) => {
    let result1;
    const enabling = enabledState === EnabledState.Disabled;
    const attVal = enabling ? "1" : "0";
    timer.current = window.setTimeout(() => {
      timer.current = 0;
      actions.updateEnabledState(
        enabling ? EnabledState.Enabled : EnabledState.Disabled
      );
      return () => {
        clearTimeout(timer.current);
      };
    }, timeout);
    if (policy) {
      const { __typename, ...everythingExceptTypename } = policy;
      const policyInput: PolicyInput = { ...everythingExceptTypename };
      policyInput.modifiedBy = user?.email;
      policyInput.isExecuteImmediately = true;
      const attrs = policy.configurationAttributes;
      if (attrs) {
        let newAttrs = [];
        let foundAttr: boolean = false;
        for (let i = 0; i < attrs.length; i++) {
          const attr = attrs[i];

          if (attr) {
            const { __typename, ...everythingExceptTypename } = attr;
            let newAttr = { ...everythingExceptTypename };
            if (attr.name === "diags.pcap.enabled") {
              foundAttr = true;
              newAttr.currentValue = attVal;
            }
            newAttrs[i] = newAttr;
          }
        }
        if (!foundAttr) {
          newAttrs.push({
            name: "diags.pcap.enabled",
            currentValue: attVal,
            paramArray: null,
            param2DArray: null
          });
        }

        policyInput.configurationAttributes = newAttrs;
      }

      result1 = await setPCAPEnabled({ tenantId, policy: policyInput });
    } else {
      const newPolicy: PolicyInput = {
        name: deviceModel + " (" + deviceId + ") Settings Policy",
        tenantId: tenantId,
        deviceId: deviceId,
        deviceType: deviceModel,
        deviceModelUuid: deviceId,
        deviceModel: deviceModel,
        siteId: null,
        policyType: PolicyType.DeviceSettingsUpdate,
        policyDeploymentScope: PolicyDeploymentScope.Device,
        configurationAttributes: [
          {
            name: "diags.pcap.enabled",
            currentValue: attVal,
            paramArray: null,
            param2DArray: null
          }
        ],
        swUpdatePath: "",
        customSwUpdatePath: "",
        customSwSupportUpdatePath: null,
        swUpdateSource: SwUpdateSource.StandardUrl,
        modifiedBy: user?.email,
        isExecuteImmediately: true
      };
      result1 = await createPolicy({ tenantId: tenantId, policy: newPolicy });
    }
    if (result1.error) {
      timer.current = 0;
      addNotification({
        id: "device-pcap-enabling",
        type: "error",
        message: enabling ? t("PCAP_ENABLE_FAILED") : t("PCAP_DISABLE_FAILED"),
        dismiss: DISMISS.DEFAULT
      });
    } else {
      const result2 = await provisioningSync({ deviceId });
      if (result2.error || result2.data?.requestProvisioningSync.error) {
        timer.current = 0;
        addNotification({
          id: "device-pcap-enabling",
          type: "error",
          message: enabling
            ? t("PCAP_ENABLE_FAILED")
            : t("PCAP_DISABLE_FAILED"),
          dismiss: DISMISS.DEFAULT
        });
      }
    }
  };

  return {
    handleEnabledChangeRequested,
    enabledState,
    actions,
    timer
  };
}
